<script>
import SelectFish from '@/components/catalog/SelectFish.vue'
import SelectSource from '@/components/catalog/SelectSource.vue'

export default {
	name: 'GrownFishModal',
	components: {
		SelectFish,
		SelectSource,
	},
	props: {
		fish: {
			type: Object,
			default: null,
		},
		isNotEditable: {
			type: Boolean,
			default: false,
		},
		design_capacities: {
			type: Array,
			default() {
				return []
			},
		},
	},
	emits: ['close', 'accept'],
	data() {
		return {
			modelValue: {
				type: null,
				design_capacity: null,
				water_supply_source_id: null,
				water_supply_source_volume: null,
				actual_production_by_species: null,
				sold_volume: null,
				juvenile_production_by_age: null,
				juvenile_sold: null,
				broodstock_production: null,
				broodstock_sold: null,
				feed_consumption_tons: null,
				feed_info: null,
			},
		}
	},
	computed: {
		confinesValueFish() {
			return (
				this.design_capacities?.map((dc) => ({
					id: dc.fish_id,
				})) || []
			)
		},
	},
	watch: {
		'modelValue.type': function () {
			if (this.design_capacities?.length && this.modelValue?.type) {
				const dc = this.design_capacities.find((dc) => dc.fish_id === this.modelValue?.type?.id)
				if (dc) {
					this.modelValue.design_capacity = parseFloat(dc.volume)
				}
			}
		},
	},
	methods: {
		opened() {
			const fh = this.$props.fish
			if (!fh) return
			this.modelValue.type = fh?.type || null
			this.modelValue.water_supply_source_id =
				fh?.water_supply_source || fh?.water_supply_source_id || null
			this.modelValue.design_capacity = fh?.design_capacity || null
			this.modelValue.water_supply_source_volume = fh?.water_supply_source_volume || null
			this.modelValue.actual_production_by_species = fh?.actual_production_by_species || null
			this.modelValue.sold_volume = fh?.sold_volume || null
			this.modelValue.juvenile_production_by_age = fh?.juvenile_production_by_age || null
			this.modelValue.juvenile_sold = fh?.juvenile_sold || null
			this.modelValue.broodstock_production = fh?.broodstock_production || null
			this.modelValue.broodstock_sold = fh?.broodstock_sold || null
			this.modelValue.feed_consumption_tons = fh?.feed_consumption_tons || null
			this.modelValue.feed_info = fh?.feed_info || null
		},
		clear() {
			this.modelValue.type = null
			this.modelValue.water_supply_source_id = null
			this.modelValue.water_supply_source_volume = null
			this.modelValue.actual_production_by_species = null
			this.modelValue.sold_volume = null
			this.modelValue.juvenile_production_by_age = null
			this.modelValue.juvenile_sold = null
			this.modelValue.broodstock_production = null
			this.modelValue.broodstock_sold = null
			this.modelValue.feed_consumption_tons = null
			this.modelValue.feed_info = null
		},
	},
}
</script>

<template>
	<modal
		:name="'GrownFishModal'"
		:width="624"
		:min-width="624"
		height="auto"
		@opened="opened"
		@closed="clear"
	>
		<div class="p-[26px] rounded-[10px]">
			<div class="flex items-center justify-between gap-[15px] mb-[36px]">
				<h1 class="text-[#0A091D] text-[18px] font-bold leading-[1.2] uppercase">
					<span v-if="isNotEditable">{{ $t(`titles.viewingNewGrown`) }}</span>
					<span v-else-if="fish">{{ $t(`titles.editingNewGrown`) }}</span>
					<span v-else>{{ $t(`titles.addingNewGrown`) }}</span>
				</h1>
				<button @click.stop.prevent="$emit('close'), clear()">
					<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M22.5 7.5l-15 15M7.5 7.5l15 15" stroke="#52A5FC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
				</button>
			</div>
			<div class="w-full">
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.objectAquaItem`) }}
					</div>
					<div>
						<SelectFish
							:value="modelValue.type"
							:required="true"
							:disabled="isNotEditable"
							:with-title="false"
							:multiple="false"
							:confines-value="confinesValueFish"
							:placeholder="$t('filters.select_fish')"
							class="w-[195px]"
							@input="modelValue.type = $event"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.sourceWaterDelivery`) }} (кг.)
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<div></div>
						<SelectSource
							:value="modelValue.water_supply_source_id"
							:required="true"
							:disabled="isNotEditable"
							:with-title="false"
							:multiple="false"
							class="w-[195px]"
							@input="modelValue.water_supply_source_id = $event"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.sourceWaterVolume`) }}
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<input
							type="number"
							step=".01"
							min="0"
							:name="'fish'"
							:ref="'fish'"
							:disabled="isNotEditable"
							class="bg-[#ffffff] border-[1px] border-solid border-[#157FEE] rounded-[5px] py-[8px] px-[10px] min-h-[41px] w-[195px]"
							v-model="modelValue.water_supply_source_volume"
							:placeholder="modelValue.water_supply_source_volume"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.design_capacity`) }}
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<input
							type="number"
							step=".01"
							min="0"
							:name="'fish'"
							:ref="'fish'"
							:disabled="true"
							class="bg-[#ffffff] border-[1px] border-solid border-[#157FEE] rounded-[5px] py-[8px] px-[10px] min-h-[41px] w-[195px]"
							:value="modelValue.design_capacity"
							:placeholder="modelValue.design_capacity"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.actual_production_by_species`) }}
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<input
							type="number"
							step=".01"
							min="0"
							:name="'fish'"
							:ref="'fish'"
							:disabled="isNotEditable"
							class="bg-[#ffffff] border-[1px] border-solid border-[#157FEE] rounded-[5px] py-[8px] px-[10px] min-h-[41px] w-[195px]"
							v-model="modelValue.actual_production_by_species"
							:placeholder="modelValue.actual_production_by_species"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.sold_volume`) }}
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<input
							type="number"
							step=".01"
							min="0"
							:name="'fish'"
							:ref="'fish'"
							:disabled="isNotEditable"
							class="bg-[#ffffff] border-[1px] border-solid border-[#157FEE] rounded-[5px] py-[8px] px-[10px] min-h-[41px] w-[195px]"
							v-model="modelValue.sold_volume"
							:placeholder="modelValue.sold_volume"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.juvenile_production_by_age`) }}
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<input
							type="number"
							step=".01"
							min="0"
							:name="'fish'"
							:ref="'fish'"
							:disabled="isNotEditable"
							class="bg-[#ffffff] border-[1px] border-solid border-[#157FEE] rounded-[5px] py-[8px] px-[10px] min-h-[41px] w-[195px]"
							v-model="modelValue.juvenile_production_by_age"
							:placeholder="modelValue.juvenile_production_by_age"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.juvenile_sold`) }}
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<input
							type="number"
							step=".01"
							min="0"
							:name="'fish'"
							:ref="'fish'"
							:disabled="isNotEditable"
							class="bg-[#ffffff] border-[1px] border-solid border-[#157FEE] rounded-[5px] py-[8px] px-[10px] min-h-[41px] w-[195px]"
							v-model="modelValue.juvenile_sold"
							:placeholder="modelValue.juvenile_sold"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.broodstock_production`) }}
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<input
							type="number"
							step=".01"
							min="0"
							:name="'fish'"
							:ref="'fish'"
							:disabled="isNotEditable"
							class="bg-[#ffffff] border-[1px] border-solid border-[#157FEE] rounded-[5px] py-[8px] px-[10px] min-h-[41px] w-[195px]"
							v-model="modelValue.broodstock_production"
							:placeholder="modelValue.broodstock_production"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.broodstock_sold`) }}
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<input
							type="number"
							step=".01"
							min="0"
							:name="'fish'"
							:ref="'fish'"
							:disabled="isNotEditable"
							class="bg-[#ffffff] border-[1px] border-solid border-[#157FEE] rounded-[5px] py-[8px] px-[10px] min-h-[41px] w-[195px]"
							v-model="modelValue.broodstock_sold"
							:placeholder="modelValue.broodstock_sold"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.feed_consumption_tons`) }}
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<input
							type="number"
							step=".01"
							min="0"
							:name="'fish'"
							:ref="'fish'"
							:disabled="isNotEditable"
							class="bg-[#ffffff] border-[1px] border-solid border-[#157FEE] rounded-[5px] py-[8px] px-[10px] min-h-[41px] w-[195px]"
							v-model="modelValue.feed_consumption_tons"
							:placeholder="modelValue.feed_consumption_tons"
						/>
					</div>
				</div>
				<div class="w-full flex justify-between gap-[15px] items-center mb-[10px]">
					<div class="text-[#82819F] text-[10px] font-medium leading-[1.2] uppercase max-w-[330px]">
						{{ $t(`labels.feed_info`) }}
					</div>
					<div class="flex justify-end items-center gap-[10px]">
						<input
							type="number"
							step=".01"
							min="0"
							:name="'fish'"
							:ref="'fish'"
							:disabled="isNotEditable"
							class="bg-[#ffffff] border-[1px] border-solid border-[#157FEE] rounded-[5px] py-[8px] px-[10px] min-h-[41px] w-[195px]"
							v-model="modelValue.feed_info"
							:placeholder="modelValue.feed_info"
						/>
					</div>
				</div>
			</div>
			<div v-if="!isNotEditable" class="w-full">
				<button
					class="p-[15px] rounded-[10px] bg-[#00E096] text-[11px] text-white mt-[36px]"
					@click="$emit('accept', modelValue), clear()"
				>
					{{ $t(`button.save`) }}
				</button>
			</div>
		</div>
	</modal>
</template>
